import { Card, CardContent } from '@sea-snag/ui';

import { formatCents, type IncomeTotals } from '../../lib/income';

/** The four cards: gross, net after tax, the owner's share and the company's (#217). */
export function SummaryCards({
  totals,
  currency,
  taxRatePct,
}: {
  totals: IncomeTotals;
  currency: string;
  taxRatePct: number;
}) {
  const nothingSplit = totals.netCents !== 0 && totals.unallocatedNetCents === totals.netCents;
  const partialNote =
    totals.unresolvedBoats > 0
      ? `Partial: ${totals.unresolvedBoats} ${totals.unresolvedBoats === 1 ? 'boat has' : 'boats have'} no owner share or ownership type, ${formatCents(totals.unallocatedNetCents, currency)} of net unallocated`
      : null;
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <SummaryCard
        title="Gross charter revenue"
        cents={totals.grossCents}
        currency={currency}
        sub={`Discounted charter value, tax included · ${totals.bookings} ${totals.bookings === 1 ? 'booking' : 'bookings'}`}
      />
      <SummaryCard
        title="Net revenue after tax"
        cents={totals.netCents}
        currency={currency}
        sub={`After ${taxRatePct}% tax (${formatCents(totals.taxCents, currency)}): what is split`}
      />
      <SummaryCard
        title="Owner share"
        cents={nothingSplit ? null : totals.ownerCents}
        currency={currency}
        sub={partialNote ?? 'All boats set up'}
      />
      {/* The brief's label stays, partial or not: this share is not profit. */}
      <SummaryCard
        title="Charter company share"
        cents={nothingSplit ? null : totals.companyCents}
        currency={currency}
        sub="Before agency commission and operating costs"
        note={partialNote}
      />
    </div>
  );
}

function SummaryCard({
  title,
  cents,
  currency,
  sub,
  note = null,
}: {
  title: string;
  /** Null when nothing can be shown: "—", never 0. */
  cents: number | null;
  currency: string;
  sub: string;
  /** A second line under `sub`, such as what a partial figure is missing. */
  note?: string | null;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{title}</div>
        <div
          className="mt-1 text-2xl font-bold tabular-nums"
          title={cents === null ? undefined : formatCents(cents, currency, 2)}
        >
          {cents === null ? '—' : formatCents(cents, currency)}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
        {note && <div className="mt-1 text-xs text-muted-foreground">{note}</div>}
      </CardContent>
    </Card>
  );
}
